import { useCallback, useMemo, useRef } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type {
	PurchaseOrderDetail,
	RollBlankOption,
} from "../models/PurchaseOrders";

interface Props {
	items: PurchaseOrderDetail[];
	selectedId: number | null;
	onSelect: (item: PurchaseOrderDetail) => void;
	loading?: boolean;
	rollBlanks: RollBlankOption[];
}

export const PurchaseOrderDetailsTable = ({
	items,
	selectedId,
	onSelect,
	loading = false,
	rollBlanks,
}: Props) => {
	const gridRef = useRef<AgGridReact<PurchaseOrderDetail>>(null);

	const blankDwgById = useMemo(
		() => new Map(rollBlanks.map((b) => [b.Id, b.Dwg])),
		[rollBlanks],
	);

	const columnDefs = useMemo<ColDef<PurchaseOrderDetail>[]>(
		() => [
			{
				field: "ItemNumber",
				headerName: "Item #",
				width: 90,
				cellStyle: { textAlign: "center" },
			},
			{
				colId: "RollBlankDwg",
				headerName: "Roll Blank",
				width: 160,
				// The legacy procedure returns the row as stored, without resolving
				// the drawing, so in legacy read mode `RollBlankDwg` is absent and the
				// column would render empty. Resolve it from the roll blank catalog
				// the page already loads — which is what the legacy screen itself does.
				valueGetter: (p) =>
					p.data?.RollBlankDwg ??
					(p.data?.RollBlankId != null
						? (blankDwgById.get(p.data.RollBlankId) ?? "")
						: ""),
			},
			{
				field: "RollBlankDiameter",
				headerName: "Blank Diameter",
				width: 140,
				cellStyle: { textAlign: "right" },
				valueFormatter: (p) =>
					p.value != null ? Number(p.value).toFixed(3) : "",
			},
			{
				headerName: "Hardness",
				width: 120,
				cellStyle: { textAlign: "center" },
				valueGetter: (p) =>
					`${p.data?.HardnessMinimum ?? ""}/${p.data?.HardnessMaximum ?? ""}`,
			},
			{
				field: "QuantityOrdered",
				headerName: "Qty Ordered",
				width: 120,
				cellStyle: { textAlign: "center" },
			},
			{
				field: "RollsReceived",
				headerName: "Received",
				width: 110,
				cellStyle: { textAlign: "center" },
			},
			{
				field: "RollCost",
				headerName: "Cost",
				flex: 1,
				minWidth: 100,
				cellStyle: { textAlign: "right" },
				valueFormatter: (p) =>
					p.value != null ? Number(p.value).toFixed(2) : "",
			},
		],
		[blankDwgById],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<PurchaseOrderDetail>) => {
			if (event.data) onSelect(event.data);
		},
		[onSelect],
	);

	const getRowId = useCallback(
		(params: { data: PurchaseOrderDetail }) => params.data.Id.toString(),
		[],
	);

	return (
		<div style={{ height: "100%", width: "100%" }}>
			<AgGridReact<PurchaseOrderDetail>
				ref={gridRef}
				rowData={items}
				columnDefs={columnDefs}
				defaultColDef={globalDefaultColDef}
				loading={loading}
				onRowClicked={onRowClicked}
				getRowId={getRowId}
				rowSelection="single"
				suppressContextMenu
				animateRows={false}
				rowHeight={32}
				headerHeight={32}
				getRowStyle={(params) =>
					selectedId === params.data?.Id
						? { backgroundColor: "#e3f2fd" }
						: undefined
				}
			/>
		</div>
	);
};
